import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  CircularProgress,
  IconButton,
  Grid,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from "@mui/material";
import {
  ArrowBack,
  Save as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  TrendingDown as MoveToSaleIcon,
} from "@mui/icons-material";
import { AppButton } from "../../../shared/components/AppButton";
import { invoiceService } from "../../../shared/services/invoiceService";
import { accountService } from "../../../shared/services/accountService";
import { Invoice, InvoicePartialPayment } from "../../../shared/types/invoice";
import { Account } from "../../../shared/types/account";

const todayISO = () => new Date().toISOString().split("T")[0];

const fieldStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#0EA5E9" },
  },
  "& .MuiInputBase-input": {
    color: "#1f2937",
    fontSize: "15px",
    // Hide up/down arrows (spinners) for type="number"
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      "WebkitAppearance": "none",
      margin: 0,
    },
    "&[type=number]": {
      "MozAppearance": "textfield",
    },
  },
};

export const PartialPaymentInvoice: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const backTo = (location.state as { backTo?: string } | null)?.backTo || "/invoices";
  const viewPath = id ? `/invoices/view/${id}` : "/invoices";

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState<string>("");
  const [alert, setAlert] = useState({
    show: false,
    type: "success" as "success" | "error",
    message: "",
  });
  const [partialPayments, setPartialPayments] = useState<
    InvoicePartialPayment[]
  >([]);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState<string>("");
  const [editingPaymentId, setEditingPaymentId] = useState("");
  const [editForm, setEditForm] = useState({
    date: "",
    amount: "",
    accountId: "",
    remark: "",
  });
  const [form, setForm] = useState({
    date: todayISO(),
    amount: "",
    accountId: "",
    remark: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [moveToSaleOpen, setMoveToSaleOpen] = useState(false);
  const [moveToSaleType, setMoveToSaleType] = useState<'loss' | 'tds'>('loss');
  const [moveToSaleAmount, setMoveToSaleAmount] = useState<string>('');
  const [moveToSaleDate, setMoveToSaleDate] = useState<string>('');
  const [moveToSaleRemark, setMoveToSaleRemark] = useState<string>('');
  const [moveToSaleLoading, setMoveToSaleLoading] = useState(false);
  const [moveToSaleError, setMoveToSaleError] = useState<string>('');

  const totalPending = invoice
    ? (invoice.totalPendingAmount ??
      (invoice.totalAmount - (invoice.advancePaidAmount || 0) - (invoice.partialPaidAmount || 0)))
    : 0;

  const toInputDate = (raw?: string) => {
    if (!raw) return todayISO();
    const dt = new Date(raw);
    if (isNaN(dt.getTime())) return todayISO();
    return dt.toISOString().split("T")[0];
  };

  const normalizePayments = (raw: any): InvoicePartialPayment[] => {
    const items =
      raw?.data?.partialPayments ||
      raw?.data?.payments ||
      raw?.partialPayments ||
      raw?.payments ||
      raw?.data?.data ||
      raw?.data?.items ||
      raw?.items ||
      [];
    if (!Array.isArray(items)) return [];
    return items.map((p: any) => ({
      ...p,
      _id: p?._id ?? p?.id,
      id: p?.id ?? p?._id,
      date: p?.date || "",
      amount: Number(p?.amount || 0),
      accountId: String(p?.accountId?._id || p?.accountId || ""),
      accountName: p?.accountName || p?.accountId?.title || "",
      remark: p?.remark ?? "",
    }));
  };

  const loadPartialPayments = async (invoiceId: string) => {
    const partialRes = await invoiceService.getPartialPayments(invoiceId);
    const normalized = normalizePayments(partialRes);
    setPartialPayments(normalized);
  };

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setIsLoading(true);
      try {
        const [invoiceRes, accountRes] = await Promise.all([
          invoiceService.getById(id),
          accountService.list({ page: 1, limit: 500 }),
        ]);
        const inv =
          invoiceRes.data?.invoice ||
          invoiceRes.data?.result ||
          (invoiceRes as any)?.data;
        const accountData = accountRes?.data;
        const accountItems =
          accountData?.data ||
          accountData?.items ||
          (accountData as any)?.accounts ||
          [];
        setInvoice(inv || null);
        setAccounts(
          (Array.isArray(accountItems) ? accountItems : [])
            .filter((a: any) => a.isActive === true)
            .map((a: any) => ({
              ...a,
              _id: a._id ?? a.id,
            })),
        );
        await loadPartialPayments(id);
        if (inv) {
          const pending =
            inv.totalPendingAmount ?? (inv.totalAmount - (inv.advancePaidAmount || 0) - (inv.partialPaidAmount || 0));
          setForm((prev) => ({
            ...prev,
            amount: pending > 0 ? String(pending) : "",
          }));
        }
      } catch (err: any) {
        setAlert({
          show: true,
          type: "error",
          message:
            err?.response?.data?.message || "Failed to load invoice details",
        });
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [id]);

  const reloadInvoiceAndPayments = async (invoiceId: string) => {
    const invoiceRes = await invoiceService.getById(invoiceId);
    const inv =
      invoiceRes.data?.invoice ||
      invoiceRes.data?.result ||
      (invoiceRes as any)?.data;
    setInvoice(inv || null);
    await loadPartialPayments(invoiceId);
  };

  const validate = () => {
    const next: Record<string, string> = {};
    const amount = Number(form.amount);

    if (!form.date) next.date = "Date is required";
    if (!form.accountId) next.accountId = "Account is required";
    if (!amount || amount <= 0) next.amount = "Valid amount is required";
    if (amount > totalPending)
      next.amount = "Amount cannot be greater than pending amount";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id || !validate()) return;

    setIsSubmitting(true);
    try {
      const selectedAccount = accounts.find(
        (a) => (a._id || a.id) === form.accountId,
      );
      await invoiceService.partialPayment({
        invoiceId: id,
        date: form.date,
        amount: Number(form.amount),
        accountId: form.accountId,
        accountName: selectedAccount?.title || "",
        remark: form.remark.trim() || undefined,
      });
      setAlert({
        show: true,
        type: "success",
        message: "Credited payment added successfully",
      });
      setForm({
        date: todayISO(),
        amount: "",
        accountId: "",
        remark: "",
      });
      await reloadInvoiceAndPayments(id);
    } catch (err: any) {
      setAlert({
        show: true,
        type: "error",
        message:
          err?.response?.data?.message || "Failed to add credited payment",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const startEdit = (payment: InvoicePartialPayment) => {
    setEditingPaymentId(payment._id || payment.id || "");
    setEditForm({
      date: toInputDate(payment.date),
      amount: String(payment.amount || ""),
      accountId: String(payment.accountId || ""),
      remark: payment.remark || "",
    });
  };

  const saveEdit = async () => {
    if (!id || !editingPaymentId) return;
    const amount = Number(editForm.amount);
    if (!editForm.date || !editForm.accountId || !amount || amount <= 0) {
      setAlert({
        show: true,
        type: "error",
        message: "Enter valid edit details",
      });
      return;
    }

    setIsUpdating(true);
    try {
      const selectedAccount = accounts.find(
        (a) => (a._id || a.id) === editForm.accountId,
      );
      await invoiceService.updatePartialPayment(id, editingPaymentId, {
        date: editForm.date,
        amount,
        accountId: editForm.accountId,
        accountName: selectedAccount?.title || "",
        remark: editForm.remark.trim() || undefined,
      });
      setEditingPaymentId("");
      setAlert({
        show: true,
        type: "success",
        message: "Credited payment updated successfully",
      });
      await reloadInvoiceAndPayments(id);
    } catch (err: any) {
      setAlert({
        show: true,
        type: "error",
        message:
          err?.response?.data?.message || "Failed to update credited payment",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteClick = (paymentId: string) => {
    setPaymentToDelete(paymentId);
    setDeleteConfirmOpen(true);
  };

  const removePayment = async () => {
    if (!id || !paymentToDelete) return;
    setIsDeleting(paymentToDelete);
    setDeleteConfirmOpen(false);
    try {
      await invoiceService.deletePartialPayment(id, paymentToDelete);
      setAlert({
        show: true,
        type: "success",
        message: "Credited payment deleted successfully",
      });
      await reloadInvoiceAndPayments(id);
    } catch (err: any) {
      setAlert({
        show: true,
        type: "error",
        message:
          err?.response?.data?.message || "Failed to delete credited payment",
      });
    } finally {
      setIsDeleting("");
      setPaymentToDelete("");
    }
  };

  const handleOpenMoveToSale = () => {
    setMoveToSaleAmount(String(totalPending));
    setMoveToSaleType('loss');
    setMoveToSaleDate(todayISO());
    setMoveToSaleRemark('');
    setMoveToSaleError('');
    setMoveToSaleOpen(true);
  };

  const handleMoveToSaleConfirm = async () => {
    if (!id) return;
    setMoveToSaleError('');
    const amount = parseFloat(moveToSaleAmount);
    if (isNaN(amount) || amount <= 0) {
      setMoveToSaleError('Please enter a valid amount');
      return;
    }
    if (amount > totalPending) {
      setMoveToSaleError('Amount cannot be greater than pending amount');
      return;
    }
    if (!moveToSaleDate) {
      setMoveToSaleError('Please select a date');
      return;
    }
    setMoveToSaleLoading(true);
    try {
      await invoiceService.moveToSale({
        invoiceId: id,
        type: moveToSaleType,
        amount,
        date: moveToSaleDate,
        remark: moveToSaleRemark.trim() || undefined,
      });
      setAlert({ show: true, type: 'success', message: 'Invoice moved to sale successfully' });
      setMoveToSaleOpen(false);
      await reloadInvoiceAndPayments(id);
    } catch (err: any) {
      setMoveToSaleError(err.response?.data?.message || 'Failed to move invoice to sale');
    } finally {
      setMoveToSaleLoading(false);
    }
  };

  const alreadyMovedToSale = !!invoice?.moveToSaleType;
  const canMoveToSale = !alreadyMovedToSale && totalPending > 0;

  return (
    <div className="add-edit-admin-container p-[40px]">
      <div className="add-edit-admin-content">
        <div className="form-wrapper max-w-4xl">
          <div className="-ml-4 mb-2">
            <Link
              to={viewPath}
              state={{ backTo }}
              className="back-link"
            >
              <ArrowBack />
              <span className="ml-2">Back to Invoice</span>
            </Link>
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="title text-2xl sm:text-5xl">Credited Payment</div>
            {canMoveToSale && (
              <AppButton
                variant="danger"
                startIcon={<MoveToSaleIcon />}
                onClick={handleOpenMoveToSale}
                sx={{
                  px: 2,
                  minHeight: 34,
                  fontSize: "0.8rem",
                  borderRadius: "8px",
                  whiteSpace: "nowrap",
                }}
              >
                Move to Sale
              </AppButton>
            )}
            {alreadyMovedToSale && (
              <div
                className="px-3 py-1 text-xs font-semibold rounded-lg"
                style={{
                  backgroundColor: "#fffbeb",
                  color: "#a16207",
                  border: "1px solid #fde68a",
                }}
              >
                Moved to Sale ({invoice?.moveToSaleType?.toUpperCase()})
              </div>
            )}
          </div>

          <div className="form-card mt-8">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <CircularProgress size={50} sx={{ color: "#6366f1" }} />
                <p className="mt-4 text-gray-600 font-medium">
                  Loading details...
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Invoice Number
                    </label>
                    <TextField
                      fullWidth
                      value={invoice?.invoiceNumber || ""}
                      disabled
                      sx={fieldStyles}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Total Amount
                    </label>
                    <TextField
                      fullWidth
                      value={invoice?.totalAmount || 0}
                      disabled
                      sx={fieldStyles}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Advance Paid
                    </label>
                    <TextField
                      fullWidth
                      value={invoice?.advancePaidAmount || 0}
                      disabled
                      sx={fieldStyles}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Credited
                    </label>
                    <TextField
                      fullWidth
                      value={invoice?.partialPaidAmount || 0}
                      disabled
                      sx={fieldStyles}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Total Pending Amount
                    </label>
                    <TextField
                      fullWidth
                      value={totalPending}
                      disabled
                      sx={fieldStyles}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Payment Date <span className="text-red-500">*</span>
                    </label>
                    <TextField
                      fullWidth
                      type="date"
                      value={form.date}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, date: e.target.value }))
                      }
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.date}
                      helperText={errors.date}
                      sx={fieldStyles}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Amount <span className="text-red-500">*</span>
                    </label>
                    <TextField
                      fullWidth
                      type="number"
                      value={form.amount}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, amount: e.target.value }))
                      }
                      onWheel={(e) => (e.target as HTMLInputElement).blur()}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                          e.preventDefault();
                        }
                      }}
                      inputProps={{ min: 0, step: "any" }}
                      error={!!errors.amount}
                      helperText={errors.amount}
                      sx={fieldStyles}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Account Name <span className="text-red-500">*</span>
                    </label>
                    <TextField
                      select
                      fullWidth
                      value={form.accountId}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, accountId: e.target.value }))
                      }
                      error={!!errors.accountId}
                      helperText={errors.accountId}
                      SelectProps={{ displayEmpty: true }}
                      sx={fieldStyles}
                    >
                      <MenuItem value="" disabled>
                        Select Account
                      </MenuItem>
                      {accounts.map((a) => (
                        <MenuItem key={a._id || a.id} value={a._id || a.id}>
                          {a.title}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Remark
                    </label>
                    <TextField
                      fullWidth
                      value={form.remark}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, remark: e.target.value }))
                      }
                      placeholder="e.g. NEFT part payment"
                      sx={fieldStyles}
                    />
                  </Grid>
                </Grid>

                <div className="flex justify-end gap-4 pt-6 border-t border-gray-100">
                  <AppButton
                    type="button"
                    variant="cancel"
                    onClick={() =>
                      navigate(viewPath, { state: { backTo } })
                    }
                  >
                    Cancel
                  </AppButton>
                  <AppButton
                    type="submit"
                    variant="primary"
                    startIcon={<SaveIcon />}
                    loading={isSubmitting}
                  >
                    Save Credited Payment
                  </AppButton>
                </div>
              </form>
            )}
          </div>

          {!isLoading && (
            <div className="form-card mt-6">
              <p className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9] mb-4">
                Credited Payment History
              </p>
              {partialPayments.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No credited payments found.
                </p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-gray-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase">
                          Date
                        </th>
                        <th className="text-right p-3 font-semibold text-gray-600 text-xs uppercase">
                          Amount
                        </th>
                        <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase">
                          Account
                        </th>
                        <th className="text-left p-3 font-semibold text-gray-600 text-xs uppercase">
                          Remark
                        </th>
                        <th className="text-center p-3 font-semibold text-gray-600 text-xs uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {partialPayments.map((p) => {
                        const pid = p._id || p.id || "";
                        const isEditing = editingPaymentId === pid;
                        return (
                          <tr key={pid} className="border-t border-gray-100">
                            <td className="p-3 text-gray-700">
                              {isEditing ? (
                                <TextField
                                  type="date"
                                  size="small"
                                  value={editForm.date}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      date: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                toInputDate(p.date)
                              )}
                            </td>
                            <td className="p-3 text-right text-gray-700">
                              {isEditing ? (
                                <TextField
                                  type="number"
                                  size="small"
                                  value={editForm.amount}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      amount: e.target.value,
                                    }))
                                  }
                                  onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                  onKeyDown={(e) => {
                                    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                      e.preventDefault();
                                    }
                                  }}
                                  inputProps={{ min: 0, step: "any" }}
                                />
                              ) : (
                                p.amount
                              )}
                            </td>
                            <td className="p-3 text-gray-700">
                              {isEditing ? (
                                <TextField
                                  select
                                  size="small"
                                  value={editForm.accountId}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      accountId: e.target.value,
                                    }))
                                  }
                                  sx={{ minWidth: 180 }}
                                >
                                  {accounts.map((a) => (
                                    <MenuItem
                                      key={a._id || a.id}
                                      value={a._id || a.id}
                                    >
                                      {a.title}
                                    </MenuItem>
                                  ))}
                                </TextField>
                              ) : (
                                p.accountName || "-"
                              )}
                            </td>
                            <td className="p-3 text-gray-700">
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  value={editForm.remark}
                                  onChange={(e) =>
                                    setEditForm((f) => ({
                                      ...f,
                                      remark: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                p.remark || "-"
                              )}
                            </td>
                            <td className="p-3 text-center">
                              {isEditing ? (
                                <div className="flex items-center justify-center gap-2">
                                  <AppButton
                                    variant="primary"
                                    size="small"
                                    onClick={saveEdit}
                                    loading={isUpdating}
                                  >
                                    Save
                                  </AppButton>
                                  <AppButton
                                    variant="secondary"
                                    size="small"
                                    onClick={() => setEditingPaymentId("")}
                                  >
                                    Cancel
                                  </AppButton>
                                </div>
                              ) : (
                                <div className="flex items-center justify-center gap-1">
                                  <Tooltip title="Edit">
                                    <IconButton
                                      size="small"
                                      onClick={() => startEdit(p)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteClick(pid)}
                                      disabled={isDeleting === pid}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Snackbar
        open={alert.show}
        autoHideDuration={4000}
        onClose={() => setAlert((a) => ({ ...a, show: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity={alert.type} variant="filled" className="rounded-xl">
          {alert.message}
        </Alert>
      </Snackbar>

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        PaperProps={{
          style: {
            borderRadius: "16px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: "bold", fontSize: "1.25rem" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#4b5563" }}>
            Are you sure you want to delete this? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ gap: 1, p: 2 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            variant="text"
            sx={{
              color: "#6b7280",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#f3f4f6" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={removePayment}
            variant="contained"
            color="error"
            sx={{
              borderRadius: "8px",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Move to Sale Dialog */}
      <Dialog
        open={moveToSaleOpen}
        onClose={() => !moveToSaleLoading && setMoveToSaleOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.1rem', pb: 0 }}>
          Move Invoice to Sale
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {moveToSaleError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
              {moveToSaleError}
            </Alert>
          )}
          <p className="text-sm text-gray-500 mb-4">
            Pending amount of <span className="font-semibold text-gray-800">₹{totalPending}</span> will be written off.
            Choose the write-off type and enter the amount.
          </p>

          <FormLabel sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Write-off Type
          </FormLabel>
          <RadioGroup
            row
            value={moveToSaleType}
            onChange={(e) => {
              setMoveToSaleType(e.target.value as 'loss' | 'tds');
              setMoveToSaleError('');
            }}
            sx={{ mt: 0.5, mb: 3 }}
          >
            <FormControlLabel
              value="loss"
              control={<Radio size="small" />}
              label={<span className="text-sm font-medium">Loss</span>}
            />
            <FormControlLabel
              value="tds"
              control={<Radio size="small" />}
              label={<span className="text-sm font-medium">TDS</span>}
            />
          </RadioGroup>

          <TextField
            label="Date"
            type="date"
            fullWidth
            value={moveToSaleDate}
            onChange={(e) => {
              setMoveToSaleDate(e.target.value);
              setMoveToSaleError('');
            }}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' }, mb: 2 }}
          />

          <TextField
            label="Write-off Amount"
            type="number"
            fullWidth
            value={moveToSaleAmount}
            onChange={(e) => {
              setMoveToSaleAmount(e.target.value);
              setMoveToSaleError('');
            }}
            onWheel={(e) => (e.target as HTMLInputElement).blur()}
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
              }
            }}
            error={!!moveToSaleError && (moveToSaleError.includes('Amount') || moveToSaleError.includes('amount'))}
            inputProps={{ min: 0, max: totalPending, step: 'any' }}
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: '10px' },
              mb: 2,
              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                'WebkitAppearance': 'none',
                margin: 0,
              },
              '& input[type=number]': {
                'MozAppearance': 'textfield',
              },
            }}
          />

          <TextField
            label="Remark"
            fullWidth
            value={moveToSaleRemark}
            onChange={(e) => {
              setMoveToSaleRemark(e.target.value);
              setMoveToSaleError('');
            }}
            size="small"
            placeholder="Enter remark (optional)"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <AppButton
            variant="cancel"
            onClick={() => setMoveToSaleOpen(false)}
            disabled={moveToSaleLoading}
            sx={{ px: 3 }}
          >
            Cancel
          </AppButton>
          <AppButton
            variant="danger"
            onClick={handleMoveToSaleConfirm}
            loading={moveToSaleLoading}
            sx={{ px: 3 }}
          >
            Confirm
          </AppButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

